import { useCallback, useEffect, useState } from 'react';

export type Density = 'compact' | 'comfortable';

const colsKey = (userId: string) => `columnPrefs:${userId}`;
const densityKey = (userId: string) => `density:${userId}`;

function readSet(key: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? new Set(arr) : null;
  } catch {
    return null;
  }
}

function readDensity(key: string): Density | null {
  try {
    const raw = localStorage.getItem(key);
    return raw === 'compact' || raw === 'comfortable' ? raw : null;
  } catch {
    return null;
  }
}

function writeSafe(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // QuotaExceededError or other storage failure — silently ignore so the
    // in-memory state still updates. The user just loses cross-session persistence.
  }
}

export function useColumnPrefs(userId: string, defaultColumns: readonly string[]): {
  visibleCols: Set<string>;
  setVisibleCols: (s: Set<string>) => void;
  density: Density;
  setDensity: (d: Density) => void;
} {
  const [visibleCols, setVisibleColsState] = useState<Set<string>>(
    () => readSet(colsKey(userId)) ?? new Set(defaultColumns),
  );
  const [density, setDensityState] = useState<Density>(
    () => readDensity(densityKey(userId)) ?? 'comfortable',
  );

  // Re-read when userId changes (different signed-in user). Intentionally exclude
  // defaultColumns from deps — callers may pass inline literals; the fallback
  // closure value is captured at effect-run time, which is correct. localStorage
  // isn't a subscribable external store, so the set-state-in-effect pattern is
  // appropriate here.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    setVisibleColsState(readSet(colsKey(userId)) ?? new Set(defaultColumns));
    setDensityState(readDensity(densityKey(userId)) ?? 'comfortable');
  }, [userId]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const setVisibleCols = useCallback(
    (s: Set<string>) => {
      writeSafe(colsKey(userId), JSON.stringify([...s]));
      setVisibleColsState(s);
    },
    [userId],
  );
  const setDensity = useCallback(
    (d: Density) => {
      writeSafe(densityKey(userId), d);
      setDensityState(d);
    },
    [userId],
  );

  return { visibleCols, setVisibleCols, density, setDensity };
}
